// How much history a chart asks for.
//
// The readings endpoint pages newest-first on a telemetry-id cursor and has no
// time filter, so a range is expressed as "keep paging until the oldest reading
// is older than this", not as a query parameter. A page is therefore always a
// prefix of the history: a range can stop early but can never skip ahead.
//
// Ranges are bounded twice: by the preset's cutoff, and by a point budget. A
// month of minute readings is 43,200 points; loading all of it would cost the
// browser more than the chart can usefully draw, so the budget stops the load
// and the screen says so rather than pretending the range is complete.

/** A selectable history range. `all` has no cutoff and relies on the budget. */
export type RangePreset = '1h' | '6h' | '24h' | '7d' | '30d' | 'all';

/** Ranges offered on the readings screen, shortest first. */
export const RANGE_PRESETS: readonly RangePreset[] = ['1h', '6h', '24h', '7d', '30d', 'all'];

/** Readings fetched per request while filling a range. */
export const CHART_PAGE_SIZE = 1000;

/**
 * Points a chart will load before stopping.
 *
 * A canvas line is legible well below this; the limit exists so `all` cannot
 * pull an unbounded history into memory, and it is high enough that the
 * longest preset (a month of minute readings) is the range that hits it first.
 */
export const MAX_CHART_POINTS = 20_000;

/** Milliseconds in one hour, the unit the presets are written in. */
const HOUR_MS = 3_600_000;

/** How far back a preset reaches, or null for "as far as the budget allows". */
const PRESET_SPAN_MS: Record<RangePreset, number | null> = {
  '1h': HOUR_MS,
  '6h': 6 * HOUR_MS,
  '24h': 24 * HOUR_MS,
  '7d': 7 * 24 * HOUR_MS,
  '30d': 30 * 24 * HOUR_MS,
  all: null,
};

/** The catalog key naming a preset. */
export function rangeLabelKey(preset: RangePreset): string {
  return `readings.range_${preset}`;
}

/**
 * The oldest instant a preset includes, or null when it has no cutoff.
 *
 * `now` is a parameter rather than read here so the value can be memoized
 * across renders and tested without freezing the clock.
 */
export function rangeCutoff(preset: RangePreset, now: number): number | null {
  const span = PRESET_SPAN_MS[preset];
  return span === null ? null : now - span;
}

/**
 * Whether paging should stop after a page.
 *
 * Stopping is the normal end of a range, not an error: either the page reached
 * back past the cutoff, or the next page would exceed the point budget.
 */
export function rangeIsComplete(
  oldestObservedAt: string | null | undefined,
  cutoff: number | null,
  loadedPoints: number,
): boolean {
  if (loadedPoints >= MAX_CHART_POINTS) return true;
  if (cutoff === null) return false;
  if (!oldestObservedAt) return false;
  const oldest = new Date(oldestObservedAt).getTime();
  // An unparseable time cannot prove the range is covered, so paging continues
  // and the page budget remains the only stop.
  return Number.isFinite(oldest) && oldest <= cutoff;
}
